import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Icon } from "@iconify/react";
import { Dialog } from "../Dialog";
import { getMediaItem } from "../../services/media";
import {
  getFileExtension,
  getMediaText,
  getMediaUrl,
  getOfficeViewerUrl,
  getPreviewKind,
  getPreviewLabel,
  isHlsStream,
} from "../../utils/mediaPreview";

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: center;
  justify-content: center;
  width: 100%;
`;

const Frame = styled.iframe`
  width: 100%;
  height: 55vh;
  border: 1px solid var(--main-border-color);
  border-radius: 8px;
  background-color: var(--main-bg);
`;

const Player = styled.video`
  width: 100%;
  max-height: 55vh;
  border-radius: 8px;
  background-color: #000;
  outline: none;
`;

const AudioFrame = styled.div`
  width: 100%;
  padding: 20px;
  border: 1px solid var(--main-border-color);
  border-radius: 8px;
  background-color: var(--main-bg);
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: center;
`;

const Image = styled.img`
  max-width: 100%;
  max-height: 55vh;
  border-radius: 8px;
  border: 1px solid var(--main-border-color);
`;

const TextBody = styled.pre`
  width: 100%;
  max-height: 55vh;
  overflow: auto;
  margin: 0;
  padding: 14px;
  font-size: 13px;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
  color: var(--main-font-color);
  background-color: var(--main-bg);
  border: 1px solid var(--main-border-color);
  border-radius: 8px;
  box-sizing: border-box;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  width: 100%;
`;

const Badge = styled.span`
  padding: 3px 9px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  border-radius: 999px;
  color: var(--button-font-color);
  background-color: var(--button-bg);
`;

const LinkButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  font-size: 14px;
  font-weight: 600;
  border-radius: 6px;
  text-decoration: none;
  cursor: pointer;
  color: var(--main-font-color);
  border: 1px dashed var(--main-border-color);

  &:hover {
    background-color: var(--content-item-bg);
  }
`;

const Hint = styled.div`
  font-size: 13px;
  font-style: italic;
  color: #a0a6b1;
  width: 100%;
`;

const Message = styled.div`
  width: 100%;
  padding: 16px;
  font-size: 14px;
  color: #a0a6b1;
  border: 1px dashed var(--main-border-color);
  border-radius: 8px;
`;

/**
 * Preview the file behind a data item: video and audio play, images and PDFs render inline,
 * Office documents go through Microsoft's viewer, text items show their body.
 */
export const MediaPreviewDialog = ({ show, item, onClose = () => { } }) => {
  const [media, setMedia] = useState(item);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!show || !item) {
      return;
    }

    setMedia(item);
    setError(null);

    // Table rows can be a trimmed version of the record, so if there is no file or text to
    // show, fetch the full media item before giving up.
    const hasUrl = Boolean(getMediaUrl(item));
    const hasText = Boolean(getMediaText(item));

    if (hasUrl || hasText || !item.public_id) {
      return;
    }

    let cancelled = false;
    setLoading(true);

    getMediaItem(item.public_id)
      .then((full) => {
        if (cancelled) return;
        if (full) {
          setMedia({ ...item, ...full });
        } else {
          setError("Could not load this media item.");
        }
      })
      .catch((loadError) => {
        if (!cancelled) setError(loadError?.message || "Could not load this media item.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [show, item]);

  const url = getMediaUrl(media);
  const kind = getPreviewKind(media);
  const text = getMediaText(media);
  const label = getPreviewLabel(media);
  const extension = getFileExtension(url);

  const renderBody = () => {
    if (loading) {
      return <Message>Loading media…</Message>;
    }

    if (error) {
      return <Message>{error}</Message>;
    }

    switch (kind) {
      case "video":
        return (
          <>
            <Player src={url} controls playsInline preload="metadata" />
            {isHlsStream(url) && (
              <Hint>
                This is an HLS (.m3u8) stream. Safari plays it natively; if it does not start here,
                use “Open in new tab”.
              </Hint>
            )}
          </>
        );

      case "audio":
        return (
          <AudioFrame>
            <Icon icon="mdi:music-note" style={{ fontSize: "42px", color: "var(--button-bg)" }} />
            <audio src={url} controls preload="metadata" style={{ width: "100%" }} />
          </AudioFrame>
        );

      case "image":
        return <Image src={url} alt={media?.name ?? "Media"} />;

      case "pdf":
      case "document":
        return <Frame src={url} title={media?.name ?? "Document preview"} />;

      case "office":
        return (
          <>
            <Frame src={getOfficeViewerUrl(url)} title={media?.name ?? "Document preview"} />
            <Hint>
              Office files are rendered by Microsoft’s online viewer, which needs the file to be
              reachable from the public internet. If nothing appears, download it instead.
            </Hint>
          </>
        );

      case "text":
        return <TextBody>{text || "This item has no text content."}</TextBody>;

      case "link":
        return (
          <Message>
            This file type ({extension ? `.${extension}` : "unknown"}) cannot be previewed in the
            browser. Use “Open in new tab” or “Download”.
          </Message>
        );

      default:
        return (
          <Message>
            No file or text is attached to this item, so there is nothing to preview.
          </Message>
        );
    }
  };

  return (
    <Dialog
      title="Preview"
      subtitle={media?.name ?? media?.short_title ?? ""}
      show={show}
      onClose={onClose}
      onOK={onClose}
      okText="Close"
      closeText="Close"
      style={{ width: "90%", maxWidth: "1100px", height: "85vh" }}
    >
      <Row>
        <Badge>{label}</Badge>
        {url && (
          <>
            <LinkButton href={url} target="_blank" rel="noopener noreferrer">
              <Icon icon="mdi:open-in-new" /> Open in new tab
            </LinkButton>
            <LinkButton href={url} download>
              <Icon icon="mdi:download" /> Download
            </LinkButton>
          </>
        )}
      </Row>

      <Body>{renderBody()}</Body>
    </Dialog>
  );
};

export default MediaPreviewDialog;
